export const SUPPORTED_LOCALES = ["en", "es"] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_COOKIE = "twi_locale";

export function matchSupported(input: string): SupportedLocale | null {
  const lower = input.toLowerCase();
  const exact = SUPPORTED_LOCALES.find((l) => l === lower);
  if (exact) return exact;
  const prefix = lower.split("-")[0];
  return SUPPORTED_LOCALES.find((l) => l === prefix) ?? null;
}

export function readLocaleCookie(): SupportedLocale | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]*)`));
  if (!match) return null;
  return matchSupported(match[1]);
}

export function writeLocaleCookie(locale: SupportedLocale): void {
  if (typeof document === "undefined") return;
  if ("cookieStore" in window) {
    const expires = Date.now() + 60 * 60 * 24 * 365 * 1000;
    void cookieStore.set({ name: LOCALE_COOKIE, value: locale, path: "/", expires, sameSite: "lax" });
  } else {
    // biome-ignore lint: fallback for browsers without CookieStore API
    document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
  }
}

export function resolveInitialLocale(): SupportedLocale {
  const fromCookie = readLocaleCookie();
  if (fromCookie) return fromCookie;

  if (typeof navigator !== "undefined") {
    for (const lang of navigator.languages ?? [navigator.language]) {
      const matched = matchSupported(lang);
      if (matched) return matched;
    }
  }

  return "en";
}
